import { Stack, Typography } from '@mui/material'
import { forwardRef, useEffect } from 'react'
import { Button, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import {
  DeliveryAddressInput,
  Maybe,
  UserDeliveryDetailFragment,
} from '@/clients/utoniq-core/schema'
import {
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { CellFrame, Organism, OrganismFrame } from '@/core/architecture'

type LotteryDeliveryFormProps = {
  user?: Maybe<UserDeliveryDetailFragment>
  onSubmit: (value: LotteryDeliveryValue) => Promise<void>
}

export type LotteryDeliveryValue = DeliveryAddressInput & {}

const schema: UseFormProps['schema'] = o => ({
  postalCode: o
    .string()
    .regex(/^\d{7}$/)
    .label(tLabel('lotteryDeliveryInfo.postalCode')),
  prefecture: o
    .string()
    .max(150)
    .label(tLabel('lotteryDeliveryInfo.prefecture')),
  city: o.string().max(150).label(tLabel('lotteryDeliveryInfo.city')),
  line1: o.string().max(150).label(tLabel('lotteryDeliveryInfo.line1')),
  line2: o
    .string()
    .optional()
    .allow('')
    .max(150)
    .label(tLabel('deliveryInfoSetting.line2')),
})

export const LotteryDeliveryForm: Organism<LotteryDeliveryFormProps> = ({
  user,
  onSubmit,
}) => {
  const { t } = useTranslation('lotteryDeliveryInfo')
  const { showError } = useMessage()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LotteryDeliveryValue>({ schema, defaultValues })

  useEffect(() => {
    if (user) {
      reset({
        postalCode: user?.address?.postalCode ?? '',
        prefecture: user?.address?.prefecture ?? '',
        city: user?.address?.city ?? '',
        line1: user?.address?.line1 ?? '',
        line2: user?.address?.line2 ?? '',
      })
    }
  }, [user])

  const handleOnSubmit = handleSubmit(async values => {
    await onSubmit(values).catch(showError)
  })

  return (
    <OrganismFrame sx={{ py: 4 }}>
      <Typography variant="subtitle1" sx={{ pb: 4 }}>
        {t('preSaleDeliveryNote', 'お客様の情報をご入力ください。')}
      </Typography>
      <TextInputCell
        type="tel"
        textFieldProps={{
          InputProps: {
            inputProps: {
              pattern: '[0-9]*',
              inputMode: 'tel',
              sx: { px: 4, py: 3 },
            },
          },
        }}
        error={errors?.postalCode}
        {...register('postalCode')}
      />
      <TextInputCell
        type="text"
        error={errors?.prefecture}
        {...register('prefecture')}
      />
      <TextInputCell type="text" error={errors?.city} {...register('city')} />
      <TextInputCell type="text" error={errors?.line1} {...register('line1')} />
      <TextInputCell type="text" error={errors?.line2} {...register('line2')} />
      <Stack flex={1} alignItems="center" sx={{ pb: 5 }}>
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleOnSubmit}
        >
          {t('submit')}
        </Button>
      </Stack>
    </OrganismFrame>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('lotteryDeliveryInfo')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const defaultValues: LotteryDeliveryValue = {
  postalCode: '',
  city: '',
  line1: '',
  line2: '',
  prefecture: '',
}
